import React, { useMemo, useState, useEffect } from 'react'
import Picker from 'react-mobile-picker'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@/hooks/useTheme'

interface DatePickerProps {
  value: Date | null
  onChange: (date: Date) => void
  onClose?: () => void
  minDate?: Date
  maxDate?: Date
}

// Генерация массива дней для месяца
const getDaysInMonth = (year: number, month: number): number[] => {
  const daysCount = new Date(year, month + 1, 0).getDate()
  return Array.from({ length: daysCount }, (_, i) => i + 1)
}

// Генерация массива месяцев с локализацией через i18n
const getMonths = (
  t: (key: string) => string,
): { value: number; label: string }[] => {
  const months = []
  for (let i = 0; i < 12; i++) {
    const label = t(`translation:months.${i}`)
    months.push({ value: i, label })
  }
  return months
}

// Генерация массива годов
const getYears = (minYear?: number, maxYear?: number): number[] => {
  const currentYear = new Date().getFullYear()
  const startYear = minYear || currentYear - 10
  const endYear = maxYear || currentYear + 10
  return Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => startYear + i,
  )
}

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  // Инициализация значений
  const initialDate = value || new Date()
  const [pickerValue, setPickerValue] = useState({
    day: String(initialDate.getDate()),
    month: String(initialDate.getMonth()),
    year: String(initialDate.getFullYear()),
  })

  // Обновление значений при изменении value
  useEffect(() => {
    if (value) {
      setPickerValue({
        day: String(value.getDate()),
        month: String(value.getMonth()),
        year: String(value.getFullYear()),
      })
    }
  }, [value])

  // Генерация опций для picker с бесконечным скроллом для дней и месяцев
  const options = useMemo(() => {
    const selectedYear = parseInt(pickerValue.year)
    const selectedMonth = parseInt(pickerValue.month)
    const months = getMonths(t)
    const years = getYears(minDate?.getFullYear(), maxDate?.getFullYear())
    const days = getDaysInMonth(selectedYear, selectedMonth)

    // Дублируем дни и месяцы для бесконечного скролла (3 раза)
    const REPEAT_COUNT = 3
    const duplicatedDays = Array(REPEAT_COUNT)
      .fill(days)
      .flat()
      .map((d) => String(d))

    const duplicatedMonths = Array(REPEAT_COUNT)
      .fill(months)
      .flat()
      .map((m) => String(m.value))

    return {
      day: duplicatedDays,
      month: duplicatedMonths,
      year: years.map((y) => String(y)), // Годы без дублирования
      originalLengths: {
        day: days.length,
        month: months.length,
      },
    }
  }, [pickerValue.year, pickerValue.month, t, minDate, maxDate])

  // Получение меток для месяцев
  const monthLabels = useMemo(() => {
    const months = getMonths(t)
    return months.reduce(
      (acc, m) => {
        acc[String(m.value)] = m.label
        return acc
      },
      {} as Record<string, string>,
    )
  }, [t])

  // Обработка изменения значений в picker
  const handleChange = (newValue: typeof pickerValue) => {
    // Нормализуем дни и месяцы для бесконечного скролла
    const dayValue = parseInt(newValue.day)
    const normalizedDay = String(
      ((dayValue - 1) % options.originalLengths.day) + 1,
    )

    const monthValue = parseInt(newValue.month)
    const normalizedMonth = String(monthValue % options.originalLengths.month)

    const normalizedValue = {
      day: normalizedDay,
      month: normalizedMonth,
      year: newValue.year, // Год без изменений
    }

    setPickerValue(normalizedValue)

    const day = parseInt(normalizedValue.day)
    const month = parseInt(normalizedValue.month)
    const year = parseInt(normalizedValue.year)

    // Проверка валидности даты
    const daysInMonth = getDaysInMonth(year, month).length
    const validDay = Math.min(day, daysInMonth)

    // Создаем дату с началом дня (00:00:00) в локальном времени
    const newDate = new Date(year, month, validDay)
    newDate.setHours(0, 0, 0, 0)

    // Проверка ограничений
    if (minDate && newDate < minDate) {
      const minDay = minDate.getDate()
      const minMonth = minDate.getMonth()
      const minYear = minDate.getFullYear()
      setPickerValue({
        day: String(minDay),
        month: String(minMonth),
        year: String(minYear),
      })
      const minDateNormalized = new Date(minYear, minMonth, minDay)
      minDateNormalized.setHours(0, 0, 0, 0)
      onChange(minDateNormalized)
      return
    }

    if (maxDate && newDate > maxDate) {
      const maxDay = maxDate.getDate()
      const maxMonth = maxDate.getMonth()
      const maxYear = maxDate.getFullYear()
      setPickerValue({
        day: String(maxDay),
        month: String(maxMonth),
        year: String(maxYear),
      })
      const maxDateNormalized = new Date(maxYear, maxMonth, maxDay)
      maxDateNormalized.setHours(0, 0, 0, 0)
      onChange(maxDateNormalized)
      return
    }

    onChange(newDate)
  }

  return (
    <div
      style={{
        width: '100%',
        backgroundColor: theme.colors.background.screen,
        borderRadius: '16px',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Визуальная линия выделения (как на изображении) */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          height: '36px',
          marginTop: '-18px',
          borderTop: `1px solid ${theme.colors.background.secondaryGray}`,
          borderBottom: `1px solid ${theme.colors.background.secondaryGray}`,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <Picker
        value={pickerValue}
        onChange={handleChange}
        height={216}
        itemHeight={36}
        wheelMode="natural"
      >
        {/* Колонка дней */}
        <Picker.Column name="day">
          {options.day.map((day) => (
            <Picker.Item key={day} value={day}>
              {({ selected }) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '36px',
                    color: selected
                      ? theme.colors.label.default
                      : theme.colors.label.description,
                    fontSize: selected ? '18px' : '16px',
                    fontWeight: selected ? '600' : '400',
                    transition: 'all 0.2s ease',
                    opacity: selected ? 1 : 0.6,
                  }}
                >
                  {day}
                </div>
              )}
            </Picker.Item>
          ))}
        </Picker.Column>

        {/* Колонка месяцев */}
        <Picker.Column name="month">
          {options.month.map((month) => (
            <Picker.Item key={month} value={month}>
              {({ selected }) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '36px',
                    color: selected
                      ? theme.colors.label.default
                      : theme.colors.label.description,
                    fontSize: selected ? '18px' : '16px',
                    fontWeight: selected ? '600' : '400',
                    transition: 'all 0.2s ease',
                    opacity: selected ? 1 : 0.6,
                  }}
                >
                  {monthLabels[month] || month}
                </div>
              )}
            </Picker.Item>
          ))}
        </Picker.Column>

        {/* Колонка годов */}
        <Picker.Column name="year">
          {options.year.map((year) => (
            <Picker.Item key={year} value={year}>
              {({ selected }) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '36px',
                    color: selected
                      ? theme.colors.label.default
                      : theme.colors.label.description,
                    fontSize: selected ? '18px' : '16px',
                    fontWeight: selected ? '600' : '400',
                    transition: 'all 0.2s ease',
                    opacity: selected ? 1 : 0.6,
                  }}
                >
                  {year}
                </div>
              )}
            </Picker.Item>
          ))}
        </Picker.Column>
      </Picker>
    </div>
  )
}
